import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, Trash, Hourglass } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

function Entry({ e, seatsLeft, onUpdate }) {
  const [busy, setBusy] = useState("");
  const notify = async () => {
    setBusy("notify");
    try {
      await api.post(`/admin/waitlist/${e.id}/notify`);
      toast.success(`Seat-open email sent to ${e.email}`);
      onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${e.email} from this waitlist?`)) return;
    setBusy("remove");
    try {
      await api.delete(`/admin/waitlist/${e.id}`);
      toast.success("Removed from waitlist");
      onUpdate();
    } catch { toast.error("Could not remove entry"); } finally { setBusy(""); }
  };
  return (
    <tr className="border-t border-white/5" data-testid={`waitlist-entry-${e.id}`}>
      <td className="p-3 font-mono text-xs text-neutral-400">#{e.position}</td>
      <td className="p-3 text-sm">{e.name || "—"}</td>
      <td className="p-3 text-sm font-mono text-xs" data-testid={`waitlist-email-${e.id}`}>{e.email}</td>
      <td className="p-3 font-mono text-xs text-neutral-400 whitespace-nowrap">{fmtDate(e.created_at)}</td>
      <td className="p-3">
        {e.notified_at
          ? <span className="code-badge text-emerald-400 border-emerald-400/40" data-testid={`waitlist-status-${e.id}`}>Notified {fmtDate(e.notified_at)}</span>
          : <span className="code-badge text-[#FFD300] border-[#FFD300]/40" data-testid={`waitlist-status-${e.id}`}>Waiting</span>}
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        <button onClick={notify} disabled={!!busy} data-testid={`waitlist-notify-${e.id}`}
          title={seatsLeft === 0 ? "Class is still full — email will say 0 seats open" : "Send seat-open email now"}
          className="btn-ghost text-xs mr-2 disabled:opacity-50">
          <PaperPlaneTilt size={14} /> {busy === "notify" ? "Sending…" : e.notified_at ? "Notify again" : "Notify now"}
        </button>
        <button onClick={remove} disabled={!!busy} data-testid={`waitlist-remove-${e.id}`} className="btn-ghost text-xs text-red-400 disabled:opacity-50">
          <Trash size={14} />
        </button>
      </td>
    </tr>
  );
}

function SessionGroup({ g, onUpdate }) {
  const full = g.seats_left === 0;
  return (
    <div className="surface-card" data-testid={`waitlist-session-${g.session_id}`}>
      <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-white/10">
        <div>
          <div className="font-heading text-xl tracking-tight">{g.course_title}</div>
          <div className="font-mono text-xs text-neutral-400 mt-1">{g.session_label}{g.venue ? ` · ${g.venue}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className={`code-badge ${full ? "text-red-400 border-red-400/40" : "text-emerald-400 border-emerald-400/40"}`} data-testid={`waitlist-seats-${g.session_id}`}>
            {full ? "FULL" : `${g.seats_left} seat${g.seats_left === 1 ? "" : "s"} open`} · {g.enrolled_count}/{g.capacity}
          </span>
          <span className="code-badge" data-testid={`waitlist-waiting-${g.session_id}`}>{g.waiting} waiting</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left">
            <tr><th className="p-3 overline">Pos</th><th className="p-3 overline">Name</th><th className="p-3 overline">Email</th><th className="p-3 overline">Joined</th><th className="p-3 overline">Status</th><th className="p-3" /></tr>
          </thead>
          <tbody>{g.entries.map((e) => <Entry key={e.id} e={e} seatsLeft={g.seats_left} onUpdate={onUpdate} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function WaitlistAdmin({ d, onUpdate }) {
  const total = d.reduce((n, g) => n + g.waiting, 0);
  return (
    <div className="space-y-4" data-testid="admin-waitlist">
      <h2 className="font-heading text-3xl tracking-tighter">Waitlist <span className="text-neutral-500 text-base">({total} waiting across {d.length} class{d.length === 1 ? "" : "es"})</span></h2>
      <p className="text-neutral-400 text-sm">Students are emailed automatically when a seat frees up or a new date is posted. Use <strong>Notify now</strong> to send the seat-open email by hand.</p>
      {d.length === 0 ? (
        <div className="surface-card p-10 text-center text-neutral-500 font-mono text-sm" data-testid="waitlist-empty">
          <Hourglass size={28} className="mx-auto mb-3 text-neutral-600" /> No one is on a waitlist yet.
        </div>
      ) : d.map((g) => <SessionGroup key={g.session_id} g={g} onUpdate={onUpdate} />)}
    </div>
  );
}
